import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Download, X } from "lucide-react";

import { usePwaInstall } from "@/lib/pwaInstall";
import { useAuth } from "@/lib/auth";

/**
 * InstallPromptModal — port of the source component.
 * Source: `_migration_source/frontend/src/components/InstallPromptModal.tsx`
 *
 * First-time PWA install modal. Auto-fires ONLY on the root Home path (`/`)
 * so users who deep-link into `/profile`, `/card`, etc. don't get their
 * primary action covered by an overlay. Once dismissed it persists via
 * `localStorage` and never re-fires. Manual entry via `/install` (linked
 * from My Business) always works regardless.
 */

const DISMISS_KEY = "wbg_install_prompt_dismissed_v1";
let _dismissedThisSession = false;

export default function InstallPromptModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { device, browser, installed, canPromptInstall, promptInstall } = usePwaInstall();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Route gate: only auto-fire on Home. Deep-links into Profile / Card /
    // etc. must not be visually covered by an overlay.
    if (pathname !== "/") return;
    if (installed) return;
    if (_dismissedThisSession) return;
    // iOS Chrome can't install — don't tease it.
    if (browser === "chrome-ios") return;

    let cancelled = false;
    try {
      const dismissed = typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem(DISMISS_KEY)
        : null;
      if (dismissed) {
        _dismissedThisSession = true;
        return;
      }
    } catch { /* ignore */ }

    const t = setTimeout(() => {
      if (!cancelled && !_dismissedThisSession) setVisible(true);
    }, 1200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [user, installed, browser, pathname]);

  const dismiss = () => {
    _dismissedThisSession = true;
    setVisible(false);
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  const handleInstallNow = async () => {
    if (canPromptInstall) {
      const outcome = await promptInstall();
      if (outcome !== "unavailable") {
        dismiss();
        return;
      }
    }
    dismiss();
    navigate("/install");
  };

  if (!visible) return null;

  const platformLabel =
    device === "ios" ? "iPhone Home Screen"
      : device === "android" ? "Android Home Screen"
      : "Desktop";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="install-prompt-modal"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div className="relative w-full max-w-md rounded-3xl border border-border/60 bg-card p-7 text-center shadow-elevated">
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          aria-label="Close"
          data-testid="install-prompt-close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Download className="h-7 w-7" aria-hidden />
        </div>
        <h2
          className="mt-5 text-[22px] font-bold tracking-tight"
          style={{ letterSpacing: "-0.02em" }}
        >
          Install WinBizGrowth
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Add WinBizGrowth to your {platformLabel} for one-tap access — it
          opens like a regular app, with its own icon and no browser bar.
        </p>
        <button
          type="button"
          onClick={handleInstallNow}
          className="mt-6 flex h-11 w-full items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-foreground shadow-gold hover:bg-brand-gold-hover active:scale-[0.98]"
          data-testid="install-prompt-install-now"
        >
          Install Now
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="mt-3 text-sm font-semibold text-muted-foreground hover:text-foreground"
          data-testid="install-prompt-later"
        >
          Maybe Later
        </button>
      </div>
    </div>
  );
}
